import { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import { IoChevronBackOutline } from "react-icons/io5";

const PAGE_SIZE = 50;

const MyMessagesView = ({ fetchLeadCounts }) => {
  const { id: conversationId } = useParams();
  const containerRef = useRef(null);
  const location = useLocation();
  const participants = location.state?.participants || [];

  const userId = Number(localStorage.getItem("loginUserId"));
  const userType = "USER";

  const loginUserId = Number(userId);

  const otherParticipant = participants.find(
    (p) => Number(p.userId) !== loginUserId,
  );

  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const initialLoadRef = useRef(true);

  /* ---------------- FETCH MESSAGES ---------------- */
  const fetchMessages = async (pageNo = 1) => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const res = await api.get(
        `/chat/messages?conversationId=${conversationId}&page=${pageNo}&size=${PAGE_SIZE}&userId=${userId}&userType=${userType}`,
      );

      const apiMessages = res.data.data || [];

      const formatted = apiMessages
        .map((msg) => ({
          id: msg.messageId,
          text: msg.message,
          mediaUrls: msg.mediaUrls || [],
          senderId: msg.senderId,
          senderType: msg.senderType,
          sentAt: msg.sentAt, // KEEP RAW DATE
          isRight: msg.senderId === userId && msg.senderType === "USER",
          time: new Date(msg.sentAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
        }))
        // 🔥 FORCE ORDER: OLD → NEW
        .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));

      setMessages((prev) => {
        if (pageNo === 1) {
          // Initial load ONLY
          return formatted;
        }

        // Older messages → prepend
        return [...formatted, ...prev];
      });

      setHasMore(!res.data.last);
      fetchLeadCounts();
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- INITIAL LOAD ---------------- */
  useEffect(() => {
    setMessages([]);
    setPage(1);
    setHasMore(true);
    initialLoadRef.current = true;
    fetchMessages(1);
  }, [conversationId]);

  useEffect(() => {
    if (initialLoadRef.current && containerRef.current && messages.length) {
      setTimeout(() => {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
        initialLoadRef.current = false;
      }, 100);
    }
  }, [messages]);

  /* ---------------- AFTER SEND ---------------- */
  const handleMessageSent = () => {
    // fetchMessages(1);
    // setTimeout(() => {
    //   if (containerRef.current) {
    //     containerRef.current.scrollTop = containerRef.current.scrollHeight;
    //   }
    // }, 100);
  };

  const handleScroll = () => {
    if (!containerRef.current || loading || !hasMore) return;

    if (containerRef.current.scrollTop === 0) {
      const prevHeight = containerRef.current.scrollHeight;
      const nextPage = page + 1;
      setPage(nextPage);

      fetchMessages(nextPage).then(() => {
        setTimeout(() => {
          const newHeight = containerRef.current.scrollHeight;
          containerRef.current.scrollTop = newHeight - prevHeight;
        }, 50);
      });
    }
  };

  const handleOptimisticSend = (msg) => {
    setMessages((prev) => [...prev, msg]);

    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, 50);
  };

  return (
    <section className="chat-section mt-2">
      <div className="card mx-3">
        <div className="d-flex justify-content-between align-items-center p-2 bg-light">
          {/* LEFT: Back + Profile */}
          <div className="d-flex align-items-center gap-2">
            {/* Back Icon */}
            <Link to="/messaging">
              <button
                className="btn btn-sm btn-light"
                title="Back"
                style={{ padding: "4px 8px" }}
              >
                <IoChevronBackOutline size={20} />
              </button>
            </Link>

            {/* Profile Image */}
            <img
              src={
                otherParticipant?.userProfile ||
                "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_1280.png"
              }
              alt="profile"
              className="rounded-circle"
              width={40}
              height={40}
              style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            />

            {/* Name + Role */}
            <div>
              <div style={{ fontWeight: 600, lineHeight: 1.2 }}>
                {otherParticipant?.userName || "Conversation"}
              </div>
              <div style={{ fontSize: "11px", color: "#6b7280" }}>
                {otherParticipant?.userType || otherParticipant?.userRole}
              </div>
            </div>
          </div>
        </div>

        <MessageList
          messages={messages}
          loading={loading}
          containerRef={containerRef}
          onScroll={handleScroll}
        />

        <MessageInput
          conversationId={conversationId}
          onSuccess={handleMessageSent}
          onOptimisticSend={handleOptimisticSend}
        />
      </div>
    </section>
  );
};

export default MyMessagesView;
